import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'
import type { User } from '@/types'

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  register: (email: string, username: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    const res = await api.getMe()
    if (res.success && res.data) {
      setUser(res.data)
    }
  }, [])

  useEffect(() => {
    const token = api.getToken()
    if (!token) {
      setIsLoading(false)
      return
    }

    api.getMe()
      .then((res) => {
        if (res.success && res.data) {
          setUser(res.data)
        } else {
          api.setToken(null)
        }
      })
      .catch(() => api.setToken(null))
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password)
    if (res.success && res.data) {
      api.setToken(res.data.token)
      setUser(res.data.user)
      return { success: true }
    }
    return { success: false, error: res.error ?? 'Login failed' }
  }, [])

  const register = useCallback(async (email: string, username: string, password: string) => {
    const res = await api.register(email, username, password)
    if (res.success && res.data) {
      api.setToken(res.data.token)
      setUser(res.data.user)
      return { success: true }
    }
    return { success: false, error: res.error ?? 'Registration failed' }
  }, [])

  const logout = useCallback(() => {
    api.setToken(null)
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
